import * as IF from "infrontjs";

// Core
import { View } from "./View";
import { Api } from "./Api";
import { Storage } from "./Storage";
import { Theme } from "./Theme";
import { Preview } from "./Preview";
import { Weather } from "./Weather";
import { Events } from "./Events";
import { Region } from "./Region";
import { Planner } from "./Planner";
import { Chat } from "./Chat";
import { Tracker } from "./Tracker";
import { Pwa } from "./Pwa";
import { Push } from "./Push";
import { Connectivity } from "./Connectivity";
import { AccommodationSwitch } from "./AccommodationSwitch";

// i18n
import {
    DEFAULT_LANGUAGE, DICTIONARIES, registerI18n, resolveLanguage, _lcs
} from "../i18n";

// Config (api endpoint, version, theme base)
import config from "../config.json";

// States — auth
import { LoginState  } from "../states/auth/LoginState";
import { SignupState } from "../states/auth/SignupState";
import { LogoutState } from "../states/auth/LogoutState";
import { TokenState  } from "../states/auth/TokenState";
import { ForgotPasswordState } from "../states/auth/ForgotPasswordState";
import { PasswordResetState  } from "../states/auth/PasswordResetState";

// States — in-app (tabs)
import { HomeState   } from "../states/home/HomeState";
import { RegionState } from "../states/region/RegionState";
import { RegionDetailState } from "../states/region/RegionDetailState";
import { RecommendationsState } from "../states/recommendations/RecommendationsState";
import { VerifyRequiredState } from "../states/gate/VerifyRequiredState";
import { EventsState } from "../states/events/EventsState";
import { EventDetailState } from "../states/events/EventDetailState";
import { PlanState   } from "../states/plan/PlanState";
import { ChatState   } from "../states/chat/ChatState";

// States — in-app (sub-pages / overlays)
import { PlaceDetailState } from "../states/place/PlaceDetailState";
import { InfoState } from "../states/info/InfoState";
import { SpaState  } from "../states/spa/SpaState";
import { SettingsState } from "../states/settings/SettingsState";
import { InstallState } from "../states/install/InstallState";
// Guest survey — reached by link (no auth guard, see the states)
import { SurveyState   } from "../states/survey/SurveyState";
import { FeedbackState } from "../states/survey/FeedbackState";
// Unknown route (old / mistyped link)
import { NotFoundState } from "../states/notfound/NotFoundState";

/**
 * App — the BELTURO Guest App.
 *
 * Mobile-first PWA opened by the guest (typically via a QR code during their
 * stay). Mirrors the saas-admin shell pattern: a custom App holds `this.user`
 * (null = logged out) and a custom View renders a single <app-layout> host
 * that branches auth ↔ in-app on `app.user`. The difference is the shell:
 * here it's a phone-shaped single column with a bottom tab bar + side drawer,
 * not a sidebar console.
 *
 * White-label: `this.theme` resolves the tenant from the subdomain on boot and
 * loads that tenant's CSS override (see core/Theme.js). The bundled brand is
 * the fallback.
 */
export class App extends IF.App
{
    constructor()
    {
        // Hash-mode routing (`#/region`), because the app is static files in a
        // GCS bucket behind the LB and a bucket has no SPA fallback: a hard load
        // of a clean path asks for an object that does not exist. The bucket's
        // notFoundPage serves index.html, so the app DID boot — but every deep
        // link answered 404, which breaks caching, uptime checks and crawlers.
        //
        // The LB cannot rewrite this away: routeAction/urlRewrite is a backend
        // SERVICE feature, and these are backend BUCKETS. Both forms were tested
        // against the live url-map (pathTemplateRewrite via routeRules, and
        // pathPrefixRewrite via exact pathRules) — each was accepted by the API,
        // passed `url-maps validate` under EXTERNAL and EXTERNAL_MANAGED alike,
        // and had no runtime effect whatsoever. Don't re-attempt it without
        // moving the assets behind a real serving backend.
        //
        // With the hash the document request is always "/" → 200, and a shared
        // link (EventDetailState.share() uses location.href) now resolves too.
        super( null, { router : { mode : 'hash' } } );

        // null = logged out. A signed-in guest is a real user object; a guest
        // who tapped "continue as guest" gets a lightweight anonymous user so
        // the in-app states (which require user !== null) still enter.
        this.user = null;
        this.session = null;

        // The tenant accommodation (resolved from the subdomain slug on boot,
        // null when unresolved → the app falls back to its bundled demo
        // content) and its digital Gästemappe (platform section catalog +
        // the enabled content blocks maintained in the client-admin).
        this.accommodation = null;
        this.guestbook = { sections: [], blocks: [] };

        // True when the subdomain names a house the API does not show us —
        // unknown, or a draft without a preview token. The app then shows
        // its 404 page instead of anything else (see init).
        this.accommodationNotFound = false;

        this.config = new IF.PathObject( config );

        // One bundle for every environment: on production hosts the API
        // endpoint switches to the live URL; localhost (incl. <slug>.localhost
        // tenant simulation) keeps the local dev API from config.json.
        // Resolved before Theme/Api so every consumer reads the final value.
        const host = ( window.location.hostname || "" ).toLowerCase();
        if ( ( host === "belturo.app" || host.endsWith( ".belturo.app" ) || host.endsWith( ".belturo.com" ) ) &&
             this.config.get( "apiLive" ) )
        {
            this.config.set( "api", this.config.get( "apiLive" ) );
        }

        // "Gäste-App ansehen" from the client-admin: the preview token that
        // opens a draft house (core/Preview). Before Theme and Api, which send it.
        this.preview = new Preview();

        // White-label theme (resolves tenant slug from the subdomain).
        this.theme = new Theme( this );

        // Persistent session-token store (localStorage).
        this.storage = new Storage();

        // REST client for the core-api (tenant-scoped via Theme).
        this.api = new Api( this );

        // Forecast for the accommodation's location. Restores its last known
        // value from localStorage here so the home screen can paint a
        // temperature on the first frame; the refresh happens off the boot path.
        this.weather = new Weather( this );

        // What's on during the stay: the accommodation's own events plus the
        // regional pool. Filled by resolveAccommodation (public endpoint), so
        // the home teaser and the events tab render without a further wait.
        this.events = new Events( this );

        // Restaurants, sights and routes around the house. Unlike the events
        // feed this is NOT loaded on boot: it is three separate feeds behind
        // one tab, each a few hundred kilobytes, and a guest who never leaves
        // the home screen should not pay for any of them.
        this.region = new Region( this );

        // The guest's own trip plans. Not loaded on boot either: it is the only
        // feature that needs a confirmed account, and an anonymous guest — the
        // common case during a stay — would pay for a request that can only
        // answer 403.
        this.planner = new Planner( this );

        // The conversation with the host. Like the Planer it needs a confirmed
        // account, so nothing is fetched until one is resolved — but unlike the
        // Planer it then polls in the background, because the tab bar's dot has
        // to appear wherever the guest happens to be.
        this.chat = new Chat( this );

        // Anonymous usage statistics for the host — started once the tenant
        // is known (see init), off for demo content and Do Not Track.
        this.tracker = new Tracker( this );

        // Installable app: Service Worker, updates, install prompt (core/Pwa),
        // and whether the API can be reached at all (core/Connectivity).
        this.pwa = new Pwa( this );
        this.connectivity = new Connectivity( this );

        // Push notifications from the house (core/Push): the stored
        // subscription follows the session — whose device it is.
        this.push = new Push( this );
        this.addEventListener( 'auth-user-login', () => this.push.sync() );
        this.addEventListener( 'auth-user-logout', () => this.push.sync() );

        // The other houses this guest has stayed at (menu "Unterkunft"),
        // loaded with the session — not awaited, the menu reads it on open.
        this.accommodationSwitch = new AccommodationSwitch( this );
        this.addEventListener( 'auth-user-login', () => this.accommodationSwitch.load() );
        this.addEventListener( 'auth-user-logout', () => this.accommodationSwitch.reset() );

        // Custom View: renders the <app-layout> shell + auth/in-app switch.
        this.view = new View( this );
    }

    /**
     * Configure InfrontJS's I18n and load the four dictionaries.
     *
     * Overridden because IF.App's own initL18n() constructs `new I18n( this )`
     * with no options — the `l18n` block of the App config is never read — so
     * the default language, the fallback and the browser detection can only be
     * set from here.
     *
     * Called from IF.App's constructor (i.e. from super()), before this class's
     * own constructor body has run: nothing below may touch this.storage or any
     * other field assigned there.
     */
    initL18n()
    {
        this.l18n = new IF.I18n( this, {
            defaultLanguage       : DEFAULT_LANGUAGE,
            fallbackLanguage      : DEFAULT_LANGUAGE,
            // Resolved against the languages we ship, below. I18n's own
            // detection accepts any valid ISO code, which would leave a French
            // phone in "fr" with no dictionary behind it.
            detectBrowserLanguage : false
        } );

        Object.keys( DICTIONARIES ).forEach(
            ( code ) => this.l18n.setDictionary( code, DICTIONARIES[ code ] )
        );

        // Hand the instance to the i18n module so the plain JS that renders
        // outside a template (the Region/Events label maps, toasts) can
        // translate too — templates get _lcs injected by IF.View.
        registerI18n( this.l18n );

        // Storage is stateless (every call reads localStorage), so a local one
        // is the same store as the this.storage created a moment later. With no
        // stored preference this falls through to the phone's own language,
        // which is the common case: a guest scans a QR code and never opens a
        // settings screen.
        this.setCurrentLanguage( resolveLanguage( new Storage().loadLanguage() ) );
    }

    /**
     * Apply a language to the running app: I18n plus the document's `lang`
     * attribute, which is what screen readers and the browser's own
     * hyphenation go by.
     */
    setCurrentLanguage( langCode )
    {
        this.l18n.setCurrentLanguage( langCode );
        if ( document && document.documentElement )
        {
            document.documentElement.setAttribute( "lang", this.l18n.getCurrentLanguage() );
        }
    }

    /**
     * Switch the app's language for good: persist the choice and reload.
     *
     * A reload rather than a re-render. Every screen rendered its text once,
     * and a good deal of the app's wording is baked into cached view models
     * (the event cards, the region cards, the planner board) that would each
     * have to be rebuilt. The route is in the hash and the session in
     * localStorage, so the guest lands back where they were.
     */
    switchLanguage( langCode )
    {
        const next = resolveLanguage( langCode );
        if ( next === this.l18n.getCurrentLanguage() )
        {
            return;
        }
        this.storage.saveLanguage( next );
        window.location.reload();
    }

    async init()
    {
        // Load the tenant override stylesheet (no-op for the fallback brand).
        this.theme.apply();

        this.stateManager.add(
            // auth
            LoginState,
            SignupState,
            LogoutState,
            TokenState,
            ForgotPasswordState,
            PasswordResetState,
            // tabs
            HomeState,
            RegionState,
            RegionDetailState,
            RecommendationsState,
            VerifyRequiredState,
            EventsState,
            EventDetailState,
            PlanState,
            ChatState,
            // sub-pages
            PlaceDetailState,
            InfoState,
            SpaState,
            SettingsState,
            InstallState,
            // survey (token link / menu / house QR)
            SurveyState,
            FeedbackState,
            NotFoundState
        );
        // Instead of the framework splash, which wipes the shell (see the state).
        this.stateManager.stateNotFoundClass = NotFoundState;

        // Resolve the tenant accommodation (+ its Gästemappe) and restore a
        // persisted session (if any) before the shell renders. Independent —
        // the accommodation is public content, the session is the user.
        await Promise.all( [
            this.resolveAccommodation(),
            this.resolveSession()
        ] );

        // A house that is not there (or not open to us): the 404 page and
        // nothing else — no shell, no router, no tracking. See run().
        if ( this.accommodationNotFound )
        {
            this.view.showNotFound( this.theme.getTenantSlug() );
            return;
        }

        // Before the first state change, so the landing screen counts too.
        this.tracker.start();

        // View.init() renders the layout and switches to auth/in-app by user.
        this.view.init();

        // Opened through "Gäste-App ansehen" while the house is a draft.
        if ( this.accommodation?.isPreview )
        {
            this.view.showPreviewNote();
        }

        // Start the chat's background poll HERE, not inside resolveSession.
        // The chat needs both the user and the tenant, and those two resolve
        // concurrently above — started from inside the session branch it would
        // simply do nothing whenever the session won the race, and the Chat
        // tab's dot would then never appear until the guest opened the chat.
        // Not awaited: the shell must not wait on it.
        this.chat.startPolling();

        // After the shell exists: their banners live inside the device frame.
        this.pwa.start();
        this.connectivity.start();
        // Not awaited; re-posts an existing subscription, never asks.
        this.push.sync();
        this.bindLifecycle();
    }

    /**
     * Foreground / background.
     *
     * A phone keeps the installed app alive in the background for hours, so
     * "opening" it is mostly coming back to a page that never reloaded. Hidden:
     * stop the chat poll (nobody sees the dot, and a background tab's timers
     * are throttled anyway). Visible again — or restored from the back/forward
     * cache: resume it with an immediate check, look for a new version, and
     * re-probe the connection if it was gone. Nothing reloads; the screen the
     * guest left is still there. Bound once (init runs once).
     */
    bindLifecycle()
    {
        const foreground = () =>
        {
            this.chat.startPolling();
            this.pwa.checkForUpdate();
            if ( !this.connectivity.online )
            {
                this.connectivity.check();
            }
        };

        document.addEventListener( "visibilitychange", () =>
        {
            if ( document.visibilityState === "visible" )
            {
                foreground();
            }
            else
            {
                this.chat.stopPolling();
            }
        } );
        window.addEventListener( "pageshow", ( e ) => { if ( e.persisted ) foreground(); } );

        // Back online: let what waited catch up.
        this.addEventListener( "app-online", () =>
        {
            this.chat.refreshUnread();
            this.tracker.flush();
        } );
    }

    async run( route = null )
    {
        await this.init();
        if ( this.accommodationNotFound )
        {
            return;
        }
        await super.run( route );
    }

    /**
     * Continue without an account ("Als Gast fortfahren"). The guest-app is
     * usable anonymously during a stay — we mint a local anonymous user so the
     * in-app states enter, but persist nothing server-side.
     */
    enterAsGuest()
    {
        this.user = {
            id       : null,
            guest    : true,
            name     : _lcs( "Gast" ),
            email    : "",
            initials : "G"
        };
        this.session = null;
        this.dispatchEvent( new CustomEvent( 'auth-user-login' ) );
        this.view.switchToInApp();
        // Switching the shell only swaps the chrome — navigate so a content
        // state actually renders into the (now present) page-content slot.
        this.router.redirect( "/" );
    }

    /**
     * Tear down the current session: clear the local token, drop the API auth
     * header, forget the user, then (for real sessions) tell the backend.
     */
    async destroySession()
    {
        const wasGuest = !!( this.user && this.user.guest );

        this.storage.deleteSessionToken();
        this.api.deleteSessionToken();
        this.user = null;
        this.session = null;
        // Stop polling and forget the thread — the next person to sign in on
        // this device must not inherit a dot, let alone a conversation.
        this.chat.reset();
        this.dispatchEvent( new CustomEvent( 'auth-user-logout' ) );

        let result = null;
        if ( !wasGuest )
        {
            try
            {
                ( { result } = await this.api.delete( '/auth/session' ) );
            }
            catch ( e )
            {
                console.error( e );
            }
        }

        this.view.switchToAuth();
        return result;
    }

    /**
     * Resolve the tenant accommodation from the subdomain slug (mirrors how
     * resolveSession restores the user) and load its digital Gästemappe.
     * Both come from public endpoints — the guest-app must work without a
     * session ("Als Gast fortfahren"). Failures leave the demo fallback
     * content in place; they never block the app.
     */
    async resolveAccommodation()
    {
        this.accommodation = null;
        this.guestbook = { sections: [], blocks: [] };
        this.accommodationNotFound = false;

        const slug = this.theme.getTenantSlug();
        if ( !slug )
        {
            return;
        }

        try
        {
            const { result } = await this.api.get( '/accommodation/slug/' + encodeURIComponent( slug ) );
            if ( result && 200 === result.status && result.data )
            {
                this.accommodation = result.data;
            }
        }
        catch ( e )
        {
            // 404: no such house, or a draft (guests never see one). Anything
            // else — offline, a server hiccup — keeps the old fallback.
            if ( +e?.status === 404 )
            {
                this.accommodationNotFound = true;
            }
            else
            {
                console.error( e );
            }
        }

        if ( !this.accommodation )
        {
            return;
        }

        // White-label: match the browser/PWA chrome to the tenant's brand
        // color and icon (the tenant stylesheet itself is loaded by
        // Theme.apply; logo/welcome image render where they are used).
        this.theme.applyMetaThemeColor( this.accommodation.theme?.primaryColor );
        this.theme.applyAppIcon( this.accommodation.theme?.appIconUrl );
        // Installed, the app is the house's: its name and icon (core-api manifest).
        this.theme.applyManifest( this.accommodation );

        // The event feed loads alongside the Gästemappe (one more parallel
        // public request, no extra round-trip on the boot path) and never
        // rejects — see Events.load.
        const eventsLoad = this.events.load();

        try
        {
            const [ catalogRes, blocksRes ] = await Promise.all( [
                this.api.get( '/guestbook/catalog' ),
                this.api.get( '/guestbook/' + this.accommodation.id )
            ] );

            if ( catalogRes?.result && 200 === +catalogRes.result.status )
            {
                // The platform sections come with German titles, which are
                // dictionary keys — localized once here (a language switch
                // reloads), so every screen reading them gets the guest's.
                this.guestbook.sections = ( catalogRes.result.data.sections || [] )
                    .map( ( section ) => ( { ...section, title : _lcs( section.title ) } ) );
            }
            if ( blocksRes?.result && 200 === +blocksRes.result.status && Array.isArray( blocksRes.result.data ) )
            {
                this.guestbook.blocks = blocksRes.result.data.map( ( b ) => this.localizeGuestbookBlock( b ) );
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        await eventsLoad;
    }

    /**
     * The Gästemappe as the guest sees it: the platform sections (in order),
     * each with its visible, non-empty blocks; sections without content are
     * dropped. (The API already limits guests to enabled blocks — the filter
     * here also guards a manager's session, which receives disabled ones.)
     */
    getGuestbookSections()
    {
        return this.guestbook.sections
            .map( ( section ) => ( {
                ...section,
                blocks: this.guestbook.blocks.filter( ( b ) =>
                    b.section === section.key && b.isEnabled !== false && this.isGuestbookBlockFilled( b ) )
            } ) )
            .filter( ( section ) => section.blocks.length > 0 );
    }

    /**
     * A Gästemappe block in the guest's language, when the host has a
     * translation for it (core-api GuestbookTranslationService); otherwise the
     * original. Field by field: a translation without a summary keeps the
     * original summary rather than showing none.
     *
     * Only a CURRENT translation is used. An outdated one was made from text
     * the host has since changed — a new Wi-Fi password, other check-out time —
     * and the German original is better than a fluent wrong fact. A language
     * switch reloads the app (switchLanguage), so doing this once at load is
     * enough.
     */
    localizeGuestbookBlock( block )
    {
        const t = block.translationStatus === "current" && block.translations
            ? block.translations[ this.l18n.getCurrentLanguage() ]
            : null;
        if ( !t )
        {
            return block;
        }

        const items = Array.isArray( block.items )
            ? block.items.map( ( item, i ) =>
            {
                const translated = Array.isArray( t.items ) ? t.items[ i ] : null;
                return {
                    ...item,
                    label : ( translated && translated.label ) || item.label,
                    value : ( translated && translated.value ) || item.value
                };
            } )
            : block.items;

        return {
            ...block,
            title : t.title || block.title,
            summary : t.summary || block.summary,
            body : t.body || block.body,
            items
        };
    }

    /** First visible, non-empty block of a given catalog type (or null). */
    getGuestbookBlock( type )
    {
        return this.guestbook.blocks.find( ( b ) =>
            b.type === type && b.isEnabled !== false && this.isGuestbookBlockFilled( b ) ) || null;
    }

    // A block is worth showing once it carries any real content.
    isGuestbookBlockFilled( block )
    {
        if ( block.summary && String( block.summary ).trim() ) return true;
        if ( block.body && String( block.body ).trim() ) return true;
        if ( Array.isArray( block.items ) && block.items.some( ( i ) => i.value && String( i.value ).trim() ) ) return true;
        if ( Array.isArray( block.attachments ) && block.attachments.some( ( a ) => a && a.url ) ) return true;
        return false;
    }

    /**
     * Restore a session from the stored token: validate it against the backend,
     * and on success populate this.user / this.session and enter the in-app
     * shell. Invalid/expired tokens are cleared and the app stays on auth.
     */
    async resolveSession()
    {
        this.user = null;
        this.session = null;

        const localSessionToken = this.storage.loadSessionToken();
        if ( !localSessionToken )
        {
            return;
        }

        let res = null;
        try
        {
            const { result } = await this.api.get( '/auth/session/' + localSessionToken );
            res = result;
        }
        catch ( e )
        {
            console.error( e );
        }

        if ( res && 200 === res.status )
        {
            const sessionData = res.data;
            if ( sessionData && sessionData.hasOwnProperty( 'user' ) )
            {
                this.api.setSessionToken( localSessionToken );
                this.session = sessionData;
                this.user = this.toLayoutUser( sessionData.user );
                this.dispatchEvent( new CustomEvent( 'auth-user-login' ) );
                this.view.switchToInApp();
            }
        }
        else
        {
            this.storage.deleteSessionToken();
            this.view.switchToAuth();
        }

        // Also here, for the sign-in path: LoginState calls resolveSession()
        // directly, long after the tenant was resolved on boot. Idempotent —
        // startPolling() clears any existing timer first — so the boot path
        // calling it again from init() costs nothing.
        this.chat.startPolling();
    }

    /**
     * May this user use the features that belong to a person rather than to a
     * house — the Planer and the Chat?
     *
     * Both carry something back to the guest later (an itinerary, a
     * conversation with the host), so both need an address that has been
     * proven to work. Two cases fail it, for different reasons:
     *
     *   • "Als Gast fortfahren" — there is no account at all, so there is
     *     nothing to attach a plan or a thread to.
     *   • a registered but unvalidated account — the address exists but has
     *     never been confirmed, so a host replying into it may be writing to
     *     somebody else's inbox.
     *
     * Everything else in the guest-app is house information and stays open to
     * everyone; see states/gate/VerifyRequiredState for what these two get
     * instead.
     */
    hasVerifiedAccount()
    {
        return !!this.user && this.user.guest !== true && this.user.isValidated === true;
    }

    /**
     * Map a core-api guest payload onto the plain object the shell renders
     * (name / email / room / initials).
     */
    toLayoutUser( apiUser )
    {
        const name = ( apiUser?.data?.name || apiUser?.email || "" ).trim();
        return {
            id       : apiUser?.id || null,
            guest    : false,
            name     : name || _lcs( "Gast" ),
            email    : apiUser?.email || "",
            room     : apiUser?.data?.room || "",
            // An unvalidated account may use the app — it just gets reminded
            // (see LoginState). Carried through so screens can ask without
            // reaching into the raw API payload.
            isValidated : apiUser?.status === "ok",
            initials : this._initialsFrom( name || apiUser?.email || "?" )
        };
    }

    _initialsFrom( str )
    {
        const parts = String( str ).trim().split( /\s+/ ).filter( Boolean );
        let initials = "?";
        if ( parts.length >= 2 )
        {
            initials = parts[ 0 ][ 0 ] + parts[ 1 ][ 0 ];
        }
        else if ( parts.length === 1 )
        {
            initials = parts[ 0 ].slice( 0, 2 );
        }
        return initials.toUpperCase();
    }
};
